import { useEffect, useState } from 'react';
import { Badge, Box, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import useApiKeysStore from '@/utils/use-api-keys';
import { ApiKey, ApiKeyAuditEvent } from '@/lib/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKey | null;
}

function eventBadgeVariant(eventType: string): 'success' | 'danger' | 'warning' | 'info' | undefined {
  switch (eventType) {
    case 'created':       return 'success';
    case 'revoked':       return 'danger';
    case 'rotated':       return 'warning';
    case 'renamed':
    case 'scopes_changed': return 'info';
    default:              return undefined;
  }
}

function eventLabel(eventType: string): string {
  switch (eventType) {
    case 'created':
      return 'Created';
    case 'revoked':
      return 'Revoked';
    case 'rotated':
      return 'Rotated';
    case 'renamed':
      return 'Renamed';
    case 'scopes_changed':
      return 'Scopes changed';
    default:
      return eventType;
  }
}

function metadataSummary(event: ApiKeyAuditEvent): string | null {
  if (!event.metadata) return null;
  if (event.event_type === 'renamed') {
    return `"${event.metadata.old}" → "${event.metadata.new}"`;
  }
  if (event.event_type === 'scopes_changed') {
    const oldList = Array.isArray(event.metadata.old) ? event.metadata.old.join(', ') : '';
    const newList = Array.isArray(event.metadata.new) ? event.metadata.new.join(', ') : '';
    return `${oldList} → ${newList}`;
  }
  if (event.event_type === 'rotated') {
    return `replaced key #${event.metadata.old_id} with #${event.metadata.new_id}`;
  }
  if (event.event_type === 'created' && Array.isArray(event.metadata.scopes)) {
    return `scopes: ${event.metadata.scopes.join(', ')}`;
  }
  return null;
}

export default function ApiKeyAuditDialog({ isOpen, onClose, apiKey }: Props) {
  const { workspaceId } = useParams();
  const loadAuditEvents = useApiKeysStore((s) => s.loadAuditEvents);
  const [events, setEvents] = useState<ApiKeyAuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !apiKey || !workspaceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadAuditEvents(workspaceId, apiKey.id)
      .then((list) => {
        if (!cancelled) setEvents(list);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.error || 'Failed to load audit log');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, apiKey, workspaceId, loadAuditEvents]);

  return (
    <DialogRoot
      lazyMount
      open={isOpen}
      size='lg'
      onOpenChange={(d) => (!d.open ? onClose() : null)}
    >
      <DialogBackdrop />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Audit log{apiKey ? ` — ${apiKey.name}` : ''}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading && (
            <HStack justify='center' py={6}>
              <Spinner />
            </HStack>
          )}
          {!loading && error && (
            <Text color='danger' fontSize='sm'>
              {error}
            </Text>
          )}
          {!loading && !error && events.length === 0 && (
            <Text color='fg.muted' fontSize='sm'>
              No audit events recorded for this key yet.
            </Text>
          )}
          {!loading && !error && events.length > 0 && (
            <Stack gap={3}>
              {events.map((event) => {
                const summary = metadataSummary(event);
                return (
                  <Box
                    key={event.id}
                    p={3}
                    borderWidth='1px'
                    borderRadius='md'
                    borderColor='border.muted'
                  >
                    <HStack justifyContent='space-between' alignItems='flex-start' mb={1}>
                      <Badge variant={eventBadgeVariant(event.event_type)}>
                        {eventLabel(event.event_type)}
                      </Badge>
                      <Text fontSize='xs' color='fg.muted'>
                        {new Date(event.occurred_at).toLocaleString()}
                      </Text>
                    </HStack>
                    <Stack gap={1} fontSize='sm'>
                      {event.actor_name && (
                        <Text>
                          <Text as='span' color='fg.muted'>by </Text>
                          {event.actor_name}
                        </Text>
                      )}
                      {event.ip && (
                        <Text color='fg.muted' fontSize='xs' fontFamily='mono'>
                          {event.ip}
                        </Text>
                      )}
                      {summary && (
                        <Text fontSize='xs' color='fg.muted'>
                          {summary}
                        </Text>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
